import React from 'react'
import { Sidebar, Button, Text, Input, LoadingSpinner, Heading, Select } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { comercialService } from '../../services/comercial'
import { modulesService } from '../../services/modules'
import type { ComercialCupom } from '../../types/comercial'
import { supabase } from '../../lib/supabase'
import { ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'

type StatusFiltro = 'all' | 'available' | 'used'

const formatDate = (iso: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const formatDateTime = (iso: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

type ActiveTab = 'dashboard' | 'cupons'

const Cupons: React.FC = () => {
  const [loading, setLoading] = React.useState(true)
  const [cupons, setCupons] = React.useState<ComercialCupom[]>([])
  const [query, setQuery] = React.useState('')
  const [filtroStatus, setFiltroStatus] = React.useState<StatusFiltro>('all')
  const [filtroIdScgp, setFiltroIdScgp] = React.useState('')
  const [filtroNomeCorretor, setFiltroNomeCorretor] = React.useState('')
  const [baixaCupom, setBaixaCupom] = React.useState<ComercialCupom | null>(null)
  const [observacaoBaixa, setObservacaoBaixa] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [detailCupom, setDetailCupom] = React.useState<ComercialCupom | null>(null)
  const [moduleInfo, setModuleInfo] = React.useState<{ display_name: string; description?: string | null } | null>(null)
  const [usedByName, setUsedByName] = React.useState<string | null>(null)
  const [activeTab, setActiveTab] = React.useState<ActiveTab>('dashboard')

  React.useEffect(() => {
    let cancelled = false
    const loadUser = async () => {
      if (!detailCupom?.used_by) {
        setUsedByName(null)
        return
      }
      try {
        const { data } = await supabase
          .from('profiles')
          .select('name')
          .eq('id', detailCupom.used_by)
          .single()
        if (!cancelled) {
          setUsedByName((data as { name?: string } | null)?.name ?? null)
        }
      } catch {
        if (!cancelled) setUsedByName(null)
      }
    }
    void loadUser()
    return () => {
      cancelled = true
    }
  }, [detailCupom?.used_by])

  const [currentPage, setCurrentPage] = React.useState(1)
  const [itemsPerPage] = React.useState(20)

  const load = React.useCallback(async () => {
    setLoading(true)
    const data = await comercialService.listCupons()
    setCupons(data)
    setLoading(false)
  }, [])

  React.useEffect(() => { load() }, [load])

  React.useEffect(() => {
    const loadModule = async () => {
      const result = await modulesService.getByName('comercial_cupons')
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name ?? 'Cupons',
          description: result.data.description ?? null,
        })
      }
    }
    loadModule()
  }, [])

  const filtered = React.useMemo(() => {
    let list = cupons
    if (query.trim()) {
      const q = query.toLowerCase()
      list = list.filter((c) => c.code.toLowerCase().includes(q))
    }
    if (filtroStatus === 'available') {
      list = list.filter((c) => !c.used_at)
    } else if (filtroStatus === 'used') {
      list = list.filter((c) => !!c.used_at)
    }
    if (filtroIdScgp.trim()) {
      const idStr = filtroIdScgp.trim()
      list = list.filter((c) => c.broker?.id_scgp != null && String(c.broker.id_scgp).includes(idStr))
    }
    if (filtroNomeCorretor.trim()) {
      const nome = filtroNomeCorretor.toLowerCase().trim()
      list = list.filter((c) => (c.broker?.name ?? '').toLowerCase().includes(nome))
    }
    return list
  }, [cupons, query, filtroStatus, filtroIdScgp, filtroNomeCorretor])

  const filteredSorted = React.useMemo(() => {
    return [...filtered].sort((a, b) => a.code.localeCompare(b.code, 'pt-BR'))
  }, [filtered])

  React.useEffect(() => {
    setCurrentPage(1)
  }, [query, filtroStatus, filtroIdScgp, filtroNomeCorretor])

  const totalPages = Math.ceil(filteredSorted.length / itemsPerPage) || 1
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const pageRows = filteredSorted.slice(startIndex, endIndex)

  const handlePreviousPage = () => {
    setCurrentPage((p) => Math.max(1, p - 1))
  }
  const handleNextPage = () => {
    setCurrentPage((p) => Math.min(totalPages, p + 1))
  }
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page)
  }

  const openBaixaModal = (e: React.MouseEvent, c: ComercialCupom) => {
    e.stopPropagation()
    setBaixaCupom(c)
    setObservacaoBaixa('')
  }

  const openDetailModal = (c: ComercialCupom) => {
    setDetailCupom(c)
  }

  const closeDetailModal = () => {
    setDetailCupom(null)
  }

  const closeBaixaModal = () => {
    setBaixaCupom(null)
    setObservacaoBaixa('')
  }

  const confirmarBaixa = async () => {
    if (!baixaCupom) return
    if (!observacaoBaixa.trim()) {
      return
    }
    setSubmitting(true)
    try {
      await comercialService.darBaixaCupom(baixaCupom.id, observacaoBaixa || null)
      closeBaixaModal()
      await load()
    } finally {
      setSubmitting(false)
    }
  }

  const valorExibido = (c: ComercialCupom) =>
    c.discount_type === 'percent' ? `${Number(c.value)}%` : `R$ ${Number(c.value).toFixed(2)}`

  const limparFiltros = () => {
    setQuery('')
    setFiltroStatus('all')
    setFiltroIdScgp('')
    setFiltroNomeCorretor('')
    setCurrentPage(1)
  }

  const statusLabel = (c: ComercialCupom) => (c.used_at ? 'Utilizado' : 'Disponível')

  const totalCupons = React.useMemo(() => cupons.length, [cupons])
  const totalUtilizados = React.useMemo(() => cupons.filter((c) => !!c.used_at).length, [cupons])
  const totalDisponiveis = React.useMemo(() => cupons.filter((c) => !c.used_at).length, [cupons])

  const statusChartData = React.useMemo(
    () => [
      { name: 'Disponíveis', value: totalDisponiveis },
      { name: 'Utilizados', value: totalUtilizados },
    ],
    [totalDisponiveis, totalUtilizados]
  )

  const COLORS = ['#22c55e', '#3b82f6', '#f97316', '#e11d48', '#6366f1', '#14b8a6']

  const topCorretores = React.useMemo(() => {
    const map = new Map<string, { name: string; usedCount: number }>()
    cupons.forEach((c) => {
      if (!c.used_at || !c.broker?.name) return
      const key = c.broker.name
      const current = map.get(key) ?? { name: c.broker.name, usedCount: 0 }
      current.usedCount += 1
      map.set(key, current)
    })
    return Array.from(map.values())
      .sort((a, b) => b.usedCount - a.usedCount)
      .slice(0, 10)
  }, [cupons])

  const topCuponsMaisUsados = React.useMemo(() => {
    const map = new Map<string, { code: string; usedCount: number }>()
    cupons.forEach((c) => {
      if (!c.used_at) return
      const key = c.code
      const current = map.get(key) ?? { code: c.code, usedCount: 0 }
      current.usedCount += 1
      map.set(key, current)
    })
    return Array.from(map.values())
      .sort((a, b) => b.usedCount - a.usedCount)
      .slice(0, 10)
  }, [cupons])

  const topCuponsMenosUsados = React.useMemo(() => {
    const map = new Map<string, { code: string; usedCount: number }>()
    cupons.forEach((c) => {
      const key = c.code
      const current = map.get(key) ?? { code: c.code, usedCount: 0 }
      if (c.used_at) {
        current.usedCount += 1
      }
      map.set(key, current)
    })
    const all = Array.from(map.values()).sort((a, b) => a.usedCount - b.usedCount)
    return all.slice(0, 10)
  }, [cupons])

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard moduleName="comercial_cupons" permissionType="show">
          <div className="h-full flex flex-col">
            {/* Header - título e descrição do cadastro do módulo */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                {moduleInfo?.display_name ?? 'Cupons'}
              </Heading>
              {moduleInfo?.description && (
                <Text size="sm" color="muted" className="mt-1">
                  {moduleInfo.description}
                </Text>
              )}
            </div>

            {/* Abas principais */}
            <div className="px-6 pt-4 border-b border-gray-200 dark:border-gray-700">
              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                  <button
                    type="button"
                    onClick={() => setActiveTab('dashboard')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'dashboard'
                        ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    Dashboard
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('cupons')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'cupons'
                        ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    Cupons
                  </button>
                </nav>
              </div>
            </div>

            {activeTab === 'dashboard' && (
              <>
                {/* Indicadores e filtros rápidos */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-4 text-white">
                      <div className="text-center">
                        <Text size="2xl" className="text-white font-bold mb-1">
                          {totalDisponiveis}
                        </Text>
                        <Text size="sm" className="text-white">
                          Cupons disponíveis
                        </Text>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-4 text-white">
                      <div className="text-center">
                        <Text size="2xl" className="text-white font-bold mb-1">
                          {totalUtilizados}
                        </Text>
                        <Text size="sm" className="text-white">
                          Cupons utilizados
                        </Text>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-gray-500 to-gray-600 shadow-lg rounded-lg p-4 text-white">
                      <div className="text-center">
                        <Text size="2xl" className="text-white font-bold mb-1">
                          {totalCupons}
                        </Text>
                        <Text size="sm" className="text-white">
                          Total de cupons
                        </Text>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Gráfico pizza disponíveis x utilizados */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <div className="flex items-center justify-between mb-4">
                        <Text size="lg" className="text-gray-900 dark:text-white">
                          Distribuição de cupons
                        </Text>
                        <Text size="sm" color="muted">
                          Disponíveis x Utilizados
                        </Text>
                      </div>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={statusChartData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={90}
                              label
                            >
                              {statusChartData.map((entry, index) => (
                                <Cell key={`cell-${entry.name}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Corretores que mais utilizaram */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <div className="flex items-center justify-between mb-4">
                        <Text size="lg" className="text-gray-900 dark:text-white">
                          Corretores que mais utilizaram cupons
                        </Text>
                      </div>
                      <div className="h-64">
                        {topCorretores.length === 0 ? (
                          <div className="h-full flex items-center justify-center">
                            <Text size="sm" color="muted">
                              Nenhum cupom utilizado ainda.
                            </Text>
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={topCorretores}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={70} />
                              <YAxis allowDecimals={false} />
                              <Tooltip />
                              <Bar dataKey="usedCount" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Cupons mais utilizados */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <div className="flex items-center justify-between mb-4">
                        <Text size="lg" className="text-gray-900 dark:text-white">
                          Cupons mais utilizados
                        </Text>
                      </div>
                      <div className="h-64">
                        {topCuponsMaisUsados.length === 0 ? (
                          <div className="h-full flex items-center justify-center">
                            <Text size="sm" color="muted">
                              Nenhum cupom utilizado ainda.
                            </Text>
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={topCuponsMaisUsados}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="code" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={70} />
                              <YAxis allowDecimals={false} />
                              <Tooltip />
                              <Bar dataKey="usedCount" fill="#22c55e" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>

                    {/* Cupons menos utilizados */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <div className="flex items-center justify-between mb-4">
                        <Text size="lg" className="text-gray-900 dark:text-white">
                          Cupons menos utilizados
                        </Text>
                      </div>
                      <div className="h-64">
                        {topCuponsMenosUsados.length === 0 ? (
                          <div className="h-full flex items-center justify-center">
                            <Text size="sm" color="muted">
                              Nenhum cupom cadastrado ainda.
                            </Text>
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={topCuponsMenosUsados}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="code" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={70} />
                              <YAxis allowDecimals={false} />
                              <Tooltip />
                              <Bar dataKey="usedCount" fill="#f97316" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'cupons' && (
              <>
            {/* Indicadores */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-4 text-white">
                  <div className="text-center">
                    <Text size="2xl" className="text-white font-bold mb-1">
                      {filtered.filter((c) => !c.used_at).length}
                    </Text>
                    <Text size="sm" className="text-white">Cupons disponíveis</Text>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-4 text-white">
                  <div className="text-center">
                    <Text size="2xl" className="text-white font-bold mb-1">
                      {filtered.filter((c) => !!c.used_at).length}
                    </Text>
                    <Text size="sm" className="text-white">Cupons utilizados</Text>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-gray-500 to-gray-600 shadow-lg rounded-lg p-4 text-white">
                  <div className="text-center">
                    <Text size="2xl" className="text-white font-bold mb-1">
                      {filtered.length}
                    </Text>
                    <Text size="sm" className="text-white">Total de cupons (filtro)</Text>
                  </div>
                </div>
              </div>

              {/* Filtros */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                    Código
                  </Text>
                  <Input
                    placeholder="Buscar por código..."
                    value={query}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </Text>
                  <Select
                    value={filtroStatus}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFiltroStatus(e.target.value as StatusFiltro)}
                    className="w-full"
                  >
                    <option value="all">Todos</option>
                    <option value="available">Disponível</option>
                    <option value="used">Utilizado</option>
                  </Select>
                </div>
                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                    ID SCGP (corretor)
                  </Text>
                  <Input
                    placeholder="Filtrar por ID SCGP..."
                    value={filtroIdScgp}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFiltroIdScgp(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                    Nome do corretor
                  </Text>
                  <Input
                    placeholder="Filtrar por nome..."
                    value={filtroNomeCorretor}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFiltroNomeCorretor(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="flex items-end">
                  <Button variant="outline" size="sm" onClick={limparFiltros}>
                    Limpar Filtros
                  </Button>
                </div>
              </div>
            </div>

            {/* Tabela */}
            <div className="flex-1 px-6 py-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden flex flex-col h-full min-h-0">
                <div className="flex-1 overflow-auto">
                  <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10 text-xs text-gray-500 dark:text-gray-400 uppercase">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">Código</th>
                        <th className="px-4 py-3 text-left font-medium">Valor</th>
                        <th className="px-4 py-3 text-left font-medium">Validade</th>
                        <th className="px-4 py-3 text-left font-medium">Status</th>
                        <th className="px-4 py-3 text-left font-medium">Corretor</th>
                        <th className="px-4 py-3 text-left font-medium">Classificação</th>
                        <th className="px-4 py-3 text-right font-medium w-28">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {loading ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center">
                            <LoadingSpinner />
                          </td>
                        </tr>
                      ) : pageRows.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                            Nenhum cupom encontrado.
                          </td>
                        </tr>
                      ) : (
                        pageRows.map((c) => (
                          <tr
                            key={c.id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                            onClick={() => openDetailModal(c)}
                          >
                            <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white">{c.code}</td>
                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{valorExibido(c)}</td>
                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{formatDate(c.valid_until)}</td>
                            <td className="px-4 py-2">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  c.used_at
                                    ? 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                                    : 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                                }`}
                              >
                                {statusLabel(c)}
                              </span>
                              {c.used_at && (
                                <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                                  {formatDate(c.used_at)}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                              {(() => {
                                if (!c.broker) return '—'
                                const parts = [
                                  c.broker.id_scgp != null ? String(c.broker.id_scgp) : null,
                                  c.broker.name,
                                ].filter(Boolean) as string[]
                                if (parts.length === 0) return '—'
                                const full = parts.join(' – ')
                                return full.length > 50 ? `${full.slice(0, 47)}...` : full
                              })()}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                              {c.broker?.classification || '—'}
                            </td>
                            <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                              {!c.used_at && (
                                <PermissionGuard moduleName="comercial_cupons" permissionType="update" fallback={null}>
                                  <Button variant="outline" size="sm" onClick={(e: React.MouseEvent) => openBaixaModal(e, c)}>
                                    Dar baixa
                                  </Button>
                                </PermissionGuard>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Paginação */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <Text size="sm">
                          Mostrando {startIndex + 1} a {Math.min(endIndex, filteredSorted.length)} de {filteredSorted.length} registros
                        </Text>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Anterior
                        </button>
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNumber: number
                            if (totalPages <= 5) {
                              pageNumber = i + 1
                            } else if (currentPage <= 3) {
                              pageNumber = i + 1
                            } else if (currentPage >= totalPages - 2) {
                              pageNumber = totalPages - 4 + i
                            } else {
                              pageNumber = currentPage - 2 + i
                            }
                            return (
                              <button
                                key={pageNumber}
                                type="button"
                                onClick={() => handlePageChange(pageNumber)}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                  currentPage === pageNumber
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            )
                          })}
                        </div>
                        <button
                          type="button"
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Próximo
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Detalhes do cupom */}
            {detailCupom && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                      Detalhes do cupom
                    </Heading>
                    <Button variant="ghost" onClick={closeDetailModal}>
                      Fechar
                    </Button>
                  </div>
                  <dl className="space-y-3 text-sm">
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400 font-medium">Código</dt>
                      <dd className="text-gray-900 dark:text-white mt-0.5">{detailCupom.code}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400 font-medium">Tipo de desconto</dt>
                      <dd className="text-gray-900 dark:text-white mt-0.5">
                        {detailCupom.discount_type === 'percent' ? 'Percentual' : 'Valor fixo'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400 font-medium">Valor</dt>
                      <dd className="text-gray-900 dark:text-white mt-0.5">{valorExibido(detailCupom)}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400 font-medium">Validade</dt>
                      <dd className="text-gray-900 dark:text-white mt-0.5">{formatDate(detailCupom.valid_until)}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400 font-medium">Status</dt>
                      <dd className="text-gray-900 dark:text-white mt-0.5">
                        {detailCupom.used_at ? `Utilizado em ${formatDateTime(detailCupom.used_at)}` : 'Disponível'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400 font-medium">Motivo da criação</dt>
                      <dd className="text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap">
                        {detailCupom.creation_reason || '—'}
                      </dd>
                    </div>
                    {detailCupom.broker && (
                      <>
                        <div>
                          <dt className="text-gray-500 dark:text-gray-400 font-medium">Corretor</dt>
                          <dd className="text-gray-900 dark:text-white mt-0.5">
                            {[
                              detailCupom.broker.id_scgp != null ? `ID SCGP: ${detailCupom.broker.id_scgp}` : null,
                              detailCupom.broker.name,
                            ]
                              .filter(Boolean)
                              .join(' – ') || '—'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-gray-500 dark:text-gray-400 font-medium">Classificação do corretor</dt>
                          <dd className="text-gray-900 dark:text-white mt-0.5">
                            {detailCupom.broker.classification || '—'}
                          </dd>
                        </div>
                      </>
                    )}
                    {detailCupom.used_at && (
                      <>
                        <div>
                          <dt className="text-gray-500 dark:text-gray-400 font-medium">Observação da baixa</dt>
                          <dd className="text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap">
                            {detailCupom.used_notes || '—'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-gray-500 dark:text-gray-400 font-medium">Usado em</dt>
                          <dd className="text-gray-900 dark:text-white mt-0.5">
                            {formatDateTime(detailCupom.used_at)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-gray-500 dark:text-gray-400 font-medium">Usado por</dt>
                          <dd className="text-gray-900 dark:text-white mt-0.5">
                            {usedByName || detailCupom.used_by || '—'}
                          </dd>
                        </div>
                      </>
                    )}
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400 font-medium">Criado em</dt>
                      <dd className="text-gray-900 dark:text-white mt-0.5">{formatDateTime(detailCupom.created_at)}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400 font-medium">Atualizado em</dt>
                      <dd className="text-gray-900 dark:text-white mt-0.5">{formatDateTime(detailCupom.updated_at)}</dd>
                    </div>
                  </dl>
                  {!detailCupom.used_at && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <PermissionGuard moduleName="comercial_cupons" permissionType="update" fallback={null}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            closeDetailModal()
                            setBaixaCupom(detailCupom)
                            setObservacaoBaixa('')
                          }}
                        >
                          Dar baixa no cupom
                        </Button>
                      </PermissionGuard>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Modal Dar baixa */}
            {baixaCupom && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Text size="lg" className="text-gray-900 dark:text-white">
                      Dar baixa no cupom
                    </Text>
                    <Button variant="ghost" onClick={closeBaixaModal} disabled={submitting}>
                      Fechar
                    </Button>
                  </div>
                  <Text size="sm" color="muted" className="mb-2">
                    Cupom: <strong className="text-gray-900 dark:text-white">{baixaCupom.code}</strong>
                  </Text>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Observação <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={observacaoBaixa}
                      onChange={(e) => setObservacaoBaixa(e.target.value)}
                      placeholder="Motivo ou observação da baixa manual..."
                      className="w-full h-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white resize-none"
                      maxLength={500}
                      disabled={submitting}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={closeBaixaModal} disabled={submitting}>
                      Cancelar
                    </Button>
                    <PermissionGuard moduleName="comercial_cupons" permissionType="update" fallback={null}>
                      <Button
                        variant="solid"
                        onClick={confirmarBaixa}
                        disabled={submitting || !observacaoBaixa.trim()}
                      >
                        {submitting ? <LoadingSpinner /> : 'Confirmar baixa'}
                      </Button>
                    </PermissionGuard>
                  </div>
                </div>
              </div>
            )}
              </>
            )}
          </div>
        </PermissionGuard>
      </div>
    </div>
  )
}

export default Cupons
